/* eslint-disable @typescript-eslint/explicit-module-boundary-types, deprecation/deprecation */
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap } from 'rxjs/operators';
import { HeaderLayoutElementsEnum, HomeRoutes } from '@app/enums';
import { environment } from '@environments/environment';
import { AuthService, NavigationAuthInfo } from './auth.service';

const unauthenticatedHeaderItems = [
	HeaderLayoutElementsEnum.NAME,
	HeaderLayoutElementsEnum.BUILDINFO,
];

@Injectable()
export class NavigationService {
	// default to most restrictive set of header items until we can determine the user's access level
	private defaultHeaderItems = unauthenticatedHeaderItems;

	/**
	 * Sidebar navigation open/close observable
	 */
	public sideNavOpened$ = new BehaviorSubject(false);
	public headerItems$ = new BehaviorSubject<HeaderLayoutElementsEnum[]>(this.defaultHeaderItems);

	/**
	 * Current route observable
	 */
	public route$ = new BehaviorSubject(this.router.url);
	public routeStart$ = new Subject();
	public routeCanceled$ = new Subject();
	public back$ = new Subject();

	readonly #destroyRef = inject(DestroyRef);

	constructor(
		private readonly router: Router,
		private readonly authService: AuthService,
	) {
		this.setDefaultHeaderItems();

		this.router.events
			.pipe(filter((e): e is NavigationCancel => e instanceof NavigationCancel))
			.subscribe(this.routeCanceled$);

		this.router.events
			.pipe(
				filter((e): e is NavigationEnd => e instanceof NavigationEnd),
				map((e: NavigationEnd) => e.urlAfterRedirects),
				distinctUntilChanged(),
			)
			.subscribe(this.route$);

		this.router.events
			.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart && e.navigationTrigger === 'popstate'))
			.subscribe(this.back$);

		this.router.events
			.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
			.subscribe(this.routeStart$);
	}

	private setDefaultHeaderItems(): void {
		this.authService.navigationAuthInfo$
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((authInfo: NavigationAuthInfo) => {
				if (!authInfo.isAuthenticated) {
					this.defaultHeaderItems = [...unauthenticatedHeaderItems];
				} else {
					this.defaultHeaderItems = authInfo.hasSystemAccess
						? Object.values(HeaderLayoutElementsEnum)
						: Object.values(HeaderLayoutElementsEnum).filter((item) => item !== HeaderLayoutElementsEnum.SETTINGS);
				}

				// TODO: we may need to filter out the Fee Management button, too, if this app isn't setup to link
				// to Fee Management
				this.resetHeaderLayoutElements();
			});
	}

	public get currentNavigation() {
		return this.router.getCurrentNavigation();
	}

	public toggleSideNav(): void {
		this.sideNavOpened$.next(!this.sideNavOpened$.value);
	}

	public closeSideNav(): void {
		this.sideNavOpened$.next(false);
	}

	public openSideNav(): void {
		this.sideNavOpened$.next(true);
	}

	public setHeaderLayoutElements(items: HeaderLayoutElementsEnum[]): void {
		this.headerItems$.next(items);
	}

	public removeHeaderLayoutElements(items: HeaderLayoutElementsEnum[]): void {
		this.headerItems$.next(this.defaultHeaderItems.filter((element) => !items.includes(element)));
	}

	public resetHeaderLayoutElements(): void {
		this.setHeaderLayoutElements(this.defaultHeaderItems);
	}

	/**
	 * Determines the appropriate home page for the user based on their authentication and access level,
	 * and navigates there.
	 * @returns true if navigation was successful, false otherwise
	 */
	public navigateToHomePage(): Observable<boolean> {
		return this.authService.navigationAuthInfo$.pipe(
			first(),
			switchMap((authInfo: NavigationAuthInfo) => {
				let route;
				if (!authInfo.isAuthenticated) {
					route = HomeRoutes.Login;
				} else if (authInfo.hasSystemAccess && !authInfo.hasBookkeepingAccess) {
					route = HomeRoutes.SystemSettings;
				} else {
					route = HomeRoutes.SchoolBookkeeping;
				}
				return this.router.navigateByUrl(route, { replaceUrl: true });
			}),
		);
	}

	public openInternalLinkInNewTab(link: string): void {
		window.open(link, '_blank');
	}

	public openLogin(): void {
		this.router.navigateByUrl(HomeRoutes.Login);
	}

	public openSwitchUnit(): void {
		this.router.navigateByUrl('/switch-unit');
	}

	public openSystemSettings(): void {
		this.router.navigateByUrl(HomeRoutes.SystemSettings);
	}

	public openFeeManagement(): void {
		// Once Cognito SSO is implemented, we will need to add some token handling here and possibly update
		// the environment variable to take the user directly into the Fee Management module.
		window.open(environment.FEE_MANAGEMENT_URL, '_blank');
	}

	/**
	 * Retrieves the current route
	 */
	public get route(): string {
		return this.router.url;
	}
}
